(ns practitest.frontend.integration-settings.jira-form
  (:require [reagent.core :as reagent]
            [practitest.frontend.init :as init]
            [practitest.frontend.common.utils :as utils]
            [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [re-frame.core :as re-frame]
            [practitest.frontend.integration-settings.jira-entity-mapping.events :as events]
            [practitest.frontend.integration-settings.jira-entity-mapping.subs :as entity-mapping-subs]
            [practitest.frontend.integration-settings.shared-form :as shared-form]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.components.selector2 :as selector2]
            [practitest.frontend.components.modal :as modal]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.components2.buttons :as buttons]))



(defn- get-available-jira-type-options
  "Build dropdown options for a project's Jira types. When the per-project cache is
   missing (still loading or fetch failed), fall back to chip labels from the saved
   mapping's :type_names so already-selected chips still render with their names."
  [types-for-project current-mappings project-id entity-type other-entity-enabled? mapping-type-names]
  (if-not types-for-project
    (vec (for [[id-str {n :name}] (or mapping-type-names {})]
           {:id id-str :label n}))
    (let [other-entity-type    (shared-form/get-other-entity-type entity-type)
          other-entity-map     (get-in current-mappings [(keyword project-id) other-entity-type])
          other-is-disabled?   (:disabled other-entity-map false)
          other-selected-types (if (or other-is-disabled? (not other-entity-enabled?))
                                 #{}
                                 (set (:jira_type other-entity-map [])))
          all-project-types    (concat (:requirements types-for-project [])
                                       (:issues types-for-project []))]
      (vec (remove #(contains? other-selected-types (:id %))
                   (map (fn [it] {:id (:id it) :label (:name it)})
                        all-project-types))))))


(def ^:private checkbox shared-form/checkbox)
(def ^:private toggle-switch shared-form/toggle-switch)

(defn- project-mapping-item
  [{:keys [project current-mappings entity-type other-entity-enabled?]}]
  (let [p-id-str            (name (:id project))
        types-for-project   (get @(re-frame/subscribe [::entity-mapping-subs/project-issue-types]) p-id-str)
        types-loading?      @(re-frame/subscribe [::entity-mapping-subs/project-types-loading? p-id-str])
        entity-label        (shared-form/get-entity-label entity-type)
        mapping             (get current-mappings (keyword p-id-str))
        entity-map          (get mapping entity-type)
        is-disabled?        (:disabled entity-map false)
        jira-type-options   (get-available-jira-type-options types-for-project current-mappings p-id-str
                                                              entity-type other-entity-enabled?
                                                              (:type_names mapping))
        handle-change       (fn [types]
                              (mixpanel/track "Jira Integration Settings"
                                              {"Action" (str "Changed " entity-label " Jira types")})
                              (re-frame/dispatch [::events/update-project-mapping p-id-str entity-type :jira_type types]))
        handle-toggle       (fn []
                              (mixpanel/track "Jira Integration Settings"
                                              {"Action" (str (if is-disabled? "Enable" "Disable")
                                                             " " entity-label " synchronization")})
                              (re-frame/dispatch [::events/toggle-project-entity-status p-id-str entity-type]))]
    [shared-form/project-mapping-row
     {:project-name    (:label project)
      :disabled?       is-disabled?
      :types-loading?  types-loading?
      :selected-types  (:jira_type entity-map [])
      :pt-entity       (shared-form/get-pt-entity-label entity-type)
      :type-options    jira-type-options
      :placeholder     (format-translation (get-translation "bugtracker.integration-settings.select-item-types.placeholder"
                                                            "Select %{bugtracker} types")
                                           :bugtracker "Jira")
      :on-types-change handle-change
      :on-toggle       handle-toggle}]))

(defn- sync-section-note [fields-mapping-url]
  [:p.note
   [:strong (get-translation "generic.label.note" "Note") ": "]
   (get-translation "jira-integration-settings.note.auto-sync"
                    "When an entity is synced, Title, Description, Status, Attachments, Comments will be automatically synced. If additional fields needed - go to")
   " "
   [:a {:href fields-mapping-url}
    (get-translation "jira-integration-settings.link.jira-fields-mapping" "\"JIRA project mapped fields\"")]
   " "
   (get-translation "jira-integration-settings.note.tab" "tab") "."])

(defn- synchronization-section
  [{:keys [title enabled? other-entity-enabled? projects current-mappings on-toggle entity-type project-id integration-id]}]
  (let [fields-mapping-url (str "/projects/" project-id "/integration_setting_jira_two_ways/" integration-id "/edit_jira_fields_mapping")]
    [:div.sync-section
     [:div.sync-header
      [:div.sync-title
       [:h5 title]
       [tooltip/mux-tooltip
        {:position "top"}
        [:span (shared-form/get-sync-tooltip-text entity-type "Jira")]
        (fn [props]
          [:span.icon-info
           (assoc props :class "icon-info")
           [icons/mux-info-circle]])]]
      [toggle-switch {:enabled?  enabled?
                      :on-change on-toggle}]]
     (when enabled?
       [:div.project-mappings
        (for [project projects]
          ^{:key (name (:id project))}
          [project-mapping-item
           {:project               project
            :current-mappings      current-mappings
            :entity-type           entity-type
            :other-entity-enabled? other-entity-enabled?}])
        [sync-section-note fields-mapping-url]])]))

(defn- project-selector
  [{:keys [projects-for-selector selected-projects]}]
  [:div.form-group.integration-project-selector
   [:label (format-translation (get-translation "bugtracker.integration-settings.select-project"
                                                "Select %{bugtracker} Projects to Sync")
                               :bugtracker "Jira") ":"]
   [selector2/new-select
    {:id          "project-selector"
     :multi?      true
     :tags?       false
     :value       (shared-form/filter-selected-projects projects-for-selector selected-projects)
     :items       projects-for-selector
     :key-fn      :id
     :label-fn    :label
     :placeholder (str (get-translation "bugtracker.integration-settings.select-project.placeholder" "Select projects") "...")
     :on-change   (fn [selected-set]
                    (let [selected-ids (set (map :id selected-set))]
                      (mixpanel/track "Jira Integration Settings" {"Action" "Selected Jira Projects"})
                      (re-frame/dispatch [::events/update-selected-projects selected-ids])))}]])

(defn- milestones-synchronization-section
  [{:keys [enabled? plan-supported? selected-projects-set milestones-selected-project-ids import-completed-sprints?]}]
  (let [disabled? (and (not plan-supported?) (not enabled?))]
    [:div.sync-section
     [:div.sync-header
      [:div.sync-title
       {:class (when disabled? "disabled")}
       [:h5 (get-translation "jira-integration-settings.milestones-synchronization.title"
                             "Enable Milestones Synchronization ( Sprints )")]
       [tooltip/mux-tooltip
        {:position "top"}
        [:span (if disabled?
                 (get-translation "jira-integration-settings.milestones-sync.plan-required-tooltip"
                                  "This applies only to new Corporate and Team plans. If needed, please contact your administrator.")
                 (get-translation "jira-integration-settings.milestones-sync.tooltip"
                                  "Enable synchronization of Jira Sprints with PractiTest Milestones"))]
        (fn [props]
          [:span.icon-info
           (assoc props :class "icon-info")
           [icons/mux-info-circle]])]
       [:span.recommended-label
        (get-translation "jira-integration-settings.recommended" "Recommended")]]
      [toggle-switch {:enabled?  enabled?
                      :disabled? disabled?
                      :on-change (fn [checked?]
                                   (mixpanel/track "Jira Sprint Sync"
                                                   {"Action" (str (if checked? "Enable" "Disable")
                                                                  " the Jira Sprint Sync")})
                                   (re-frame/dispatch [::events/toggle-milestones-sync checked?]))}]]
   (when enabled?
     [:div.milestones-settings
      [:div.form-group.milestones-project-selector
       [:label (get-translation "jira-integration-settings.select-sprint-projects"
                                "Select projects for Sprint synchronization")]
       [selector2/new-select
        {:multi?      true
         :tags?       false
         :value       (shared-form/filter-selected-projects selected-projects-set milestones-selected-project-ids)
         :items       selected-projects-set
         :key-fn      :id
         :label-fn    :label
         :placeholder (str (get-translation "bugtracker.integration-settings.select-project.placeholder"
                                            "Select projects") "...")
         :on-change   (fn [selected-set]
                        (let [selected-ids (set (map :id selected-set))]
                          (re-frame/dispatch [::events/update-milestones-selected-projects selected-ids])))}]]
      [:div.checkbox
       [:label
        [checkbox {:checked?  import-completed-sprints?
                   :on-change (fn [e]
                                (let [new-value (.. e -target -checked)]
                                  (mixpanel/track "Jira Sprint Sync"
                                                  {"Action" (str (if new-value "Enable" "Disable")
                                                                 " import completed sprints")})
                                  (re-frame/dispatch [::events/toggle-import-completed-sprints new-value])))}]
        (get-translation "jira-integration-settings.import-completed-sprints"
                                  "Import completed sprints")
        " "
        [:span.text-muted
         (get-translation "jira-integration-settings.import-completed-sprints.description"
                          "( Include closed / completed sprints from the last 3 months in addition to active and future sprint )")]]]])]))

(defn- general-settings-section
  [{:keys [allow-issue-creation? use-pt-id? skip-mapping-manual-import-checked?]}]
  [:div.general-settings
   [:h5 (get-translation "bugtracker.integration-settings.general-settings" "General Settings")]
   [:div.checkbox
    [:label
     [checkbox {:checked?  allow-issue-creation?
                :on-change (fn [e]
                             (let [new-value (.. e -target -checked)]
                               (re-frame/dispatch [::events/toggle-allow-issue-creation new-value])))}]
     (get-translation "bugtracker.integration-settings.checkbox.issue-creation"
                      "Allow to create Issues in PractiTest that are not linked to Integration")]]
   [:div.checkbox
    [:label
     [checkbox {:disabled? (not allow-issue-creation?)
                :checked?  use-pt-id?
                :on-change (fn [e]
                             (let [new-value (.. e -target -checked)]
                               (re-frame/dispatch [::events/toggle-use-pt-id new-value])))}]
     (get-translation "bugtracker.integration-settings.checkbox.use-pt-ids"
                      "Use PractiTest ID for synced Issues")]]
   [:div.checkbox
    [:label
     [checkbox {:checked?  skip-mapping-manual-import-checked?
                :on-change (fn [e]
                             (let [new-value (.. e -target -checked)]
                               (re-frame/dispatch [::events/toggle-skip-mapping-manual-import new-value])))}]
     (get-translation "bugtracker.integration-settings.checkbox.skip-mapping"
                      "Do not enforce mapping")
     " "
     [tooltip/mux-tooltip
      {:position "top"}
      [:span (format-translation (get-translation "bugtracker.integration-settings.skip-mapping.tooltip"
                                                   "Selecting this option will disable restrictions for mapped entities. Any %{bugtracker} entity can be synced as a requirement or an issue.")
                                 :bugtracker "Jira")]
      (fn [props]
        [:span.icon-info
         (assoc props :class "icon-info")
         [icons/mux-info-circle]])]]]])

(defn- form-actions []
  (let [saving? @(re-frame/subscribe [::entity-mapping-subs/saving?])
        dirty?  @(re-frame/subscribe [::entity-mapping-subs/dirty?])]
    [:div.form-actions
     [buttons/button
      {:text      (get-translation "generic.button.cancel" "Cancel")
       :disabled? (or saving? (not dirty?))
       :on-click  #(re-frame/dispatch [::events/cancel-changes])}]
     [buttons/button
      {:text      (if saving?
                    (str (get-translation "generic.label.saving" "Saving") "...")
                    (get-translation "jira-integration-settings.button.update-settings" "Update Settings"))
       :primary?  true
       :disabled? (or saving? (not dirty?))
       :on-click  #(re-frame/dispatch [::events/save-settings])}]]))

(defn jira-entity-mapping-form []
  (let [loading?                              @(re-frame/subscribe [::entity-mapping-subs/loading?])
        projects-loading?                     @(re-frame/subscribe [::entity-mapping-subs/projects-loading?])
        requirements-enabled?                 @(re-frame/subscribe [::entity-mapping-subs/requirements-sync-enabled?])
        issues-enabled?                       @(re-frame/subscribe [::entity-mapping-subs/issues-sync-enabled?])
        milestones-enabled?                   @(re-frame/subscribe [::entity-mapping-subs/milestones-sync-enabled?])
        milestones-selected-project-ids       @(re-frame/subscribe [::entity-mapping-subs/milestones-selected-project-ids])
        import-completed-sprints?             @(re-frame/subscribe [::entity-mapping-subs/import-completed-sprints?])
        available-projects                    @(re-frame/subscribe [::entity-mapping-subs/available-projects])
        projects-for-selector                 (vec (map shared-form/transform-project-for-selector available-projects))
        entity-mapping                        @(re-frame/subscribe [::entity-mapping-subs/entity-mapping])
        current-mappings                      (get-in entity-mapping [:data :project_mappings])
        selected-projects                     @(re-frame/subscribe [::entity-mapping-subs/selected-project-ids])
        allow-issue-creation?                 (get-in entity-mapping [:data :allow_issue_creation] false)
        use-pt-id?                            (get-in entity-mapping [:data :use_pt_id_for_synced_issues] false)
        integration-name                      @(re-frame/subscribe [::entity-mapping-subs/integration-name])
        selected-projects-set                 (shared-form/filter-selected-projects projects-for-selector selected-projects)
        project-id                            (get-in entity-mapping [:project-id])
        integration-id                        (get-in entity-mapping [:integration-id])
        milestones-plan-supported?            @(re-frame/subscribe [::entity-mapping-subs/milestones-plan-supported?])
        skip-mapping-manual-import-checked?   (get-in entity-mapping [:data :skip_mapping_in_manual_import] false)
        project-area
        (fn []
          (if projects-loading?
            [:div.loading-container.projects-loading
             [spinners/mux-spinner {:size :small}]]
            [:<>
             [project-selector
              {:projects-for-selector projects-for-selector
               :selected-projects     selected-projects}]

             [synchronization-section
              {:title                 (get-translation "bugtracker.integration-settings.requirements-synchronization.title"
                                                       "Enable Requirement Synchronization")
               :enabled?              requirements-enabled?
               :other-entity-enabled? issues-enabled?
               :on-toggle             #(re-frame/dispatch [::events/toggle-requirements-sync %])
               :projects              selected-projects-set
               :current-mappings      current-mappings
               :entity-type           :requirements
               :project-id            project-id
               :integration-id        integration-id}]

             [synchronization-section
              {:title                 (get-translation "bugtracker.integration-settings.issues-synchronization.title"
                                                       "Enable Issue Synchronization")
               :enabled?              issues-enabled?
               :other-entity-enabled? requirements-enabled?
               :on-toggle             #(re-frame/dispatch [::events/toggle-issues-sync %])
               :projects              selected-projects-set
               :current-mappings      current-mappings
               :entity-type           :issues
               :project-id            project-id
               :integration-id        integration-id}]

             [milestones-synchronization-section
              {:enabled?                        milestones-enabled?
               :plan-supported?                 milestones-plan-supported?
               :selected-projects-set           selected-projects-set
               :milestones-selected-project-ids milestones-selected-project-ids
               :import-completed-sprints?       import-completed-sprints?}]]))]

    [:div.integration-form-content
     [:div.form-group.integration-env
      [:label (format-translation (get-translation "bugtracker.integration-settings.env"
                                                   "%{bugtracker} Environment")
                                  :bugtracker "Jira") ":"]
      [:input.form-control.project-name-input {:type     "text"
                                               :value    (str integration-name)
                                               :disabled true}]]

     (if loading?
       [:div.loading-container
        [spinners/mux-spinner {:size :medium}]]
       [:<>
        [project-area]
        [general-settings-section
         {:allow-issue-creation?                 allow-issue-creation?
          :use-pt-id?                            use-pt-id?
          :skip-mapping-manual-import-checked?   skip-mapping-manual-import-checked?}]
        [form-actions]])]))

(defn- jira-user-modal [{:keys [container-ref]}]
  (let [clicked-url (reagent/atom nil)]
    (fn [{:keys [container-ref]}]
      (let [url          @(re-frame/subscribe [::entity-mapping-subs/jira-user-login-url])
            link-clicked? (and url (= @clicked-url url))]
        [modal/bootstrap-modal
         {:is-open?         (boolean url)
          :on-request-close (constantly nil)
          :container-ref    container-ref
          :centered?        true
          :close-button?    false}
         [:span (get-translation "jira-integration-settings.jira-user-modal.title" "Link Jira User")]
         [:div
          (get-translation "jira-integration-settings.jira-user-modal.no-user" "No JIRA user is linked. Please ")
          [:a {:href     url
               :target   "_blank"
               :on-click #(reset! clicked-url url)}
           (get-translation "generic.label.click-here" "click here")]
          (get-translation "jira-integration-settings.jira-user-modal.link-user" " to link your JIRA user.")
          [:br]
          (get-translation "jira-integration-settings.jira-user-modal.once-required"
                           "This operation is required only once in order to set your JIRA account.")
          [:br]
          (get-translation "jira-integration-settings.jira-user-modal.click-ok" "Click 'OK' once you finished.")]
         [buttons/button
          {:on-click  #(re-frame/dispatch [::events/dismiss-jira-user-modal])
           :text      (get-translation "generic.button.ok" "OK")
           :primary?  true
           :disabled? (not link-clicked?)}]]))))

(defn jira-integration-form-root [_]
  (let [container-ref (atom nil)]
    (fn [_]
      [:div {:ref (dom/ref-handler container-ref)}
       [jira-entity-mapping-form]
       [jira-user-modal {:container-ref container-ref}]])))

(defn ^:export init [webcomponent options]
  (init/init! {:options       options
               :init-event-fn (fn [config]
                                [[::events/initialize-db config]
                                 [::events/fetch-entity-mapping]
                                 [::events/fetch-available-projects]])
               :webcomponent  webcomponent
               :root-view     jira-integration-form-root}))

(defn ^:dev/after-load remount []
  (init/remount-roots "jira-integration-form-component"
                      jira-integration-form-root))

(utils/register-webcomponent! "jira-integration-form-component")
